import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Row, Col, Modal, Form, DatePicker, message } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined, EyeOutlined, FilePdfOutlined, FileExcelOutlined  } from '@ant-design/icons';
import { MdEvent } from 'react-icons/md'; // Changed icon for events
import moment from 'moment'; // Import moment
import api from '../api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const EventRegistration = () => {
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/event');
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data.map(item => ({
          key: item._id,
          eventName: item.eventName,
          eventlocation: item.eventlocation,
          eventDate: item.eventDate,
          createdAt: item.createdAt,
          incharge_1: item.incharge_1,
          incharge_2: item.incharge_2,
          incharge_3: item.incharge_3,
        })));
      } else {
        throw new Error('Unexpected response structure');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('Failed to fetch data. Please try again later.');
    }
  };

  const handleAdd = async (values) => {
    try {
      const endpoint = editMode
        ? '/event/updateEvent'
        : '/event/addEvent';

      const method = editMode ? 'put' : 'post';
      const payload = editMode ? { ...values, id: currentRecord.key } : values;

      await api({
        method,
        url: endpoint,
        data: payload
      });

      message.success(editMode ? 'Event updated successfully!' : 'Event added successfully!');
      fetchData(); // Refresh the data
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Failed to handle event:', error);
      message.error(editMode ? 'Failed to update event. Please try again later.' : 'Failed to add event. Please try again later.');
    }
  };

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        const formattedDate = values.eventDate ? (values.eventDate.toDate ? moment(values.eventDate.toDate()).format('YYYY-MM-DD') : moment(values.eventDate).format('YYYY-MM-DD')) : null;
        const updatedValues = { ...values, eventDate: formattedDate };

        handleAdd(updatedValues);
      })
      .catch(info => console.log('Validate Failed:', info));
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddEventClick = () => {
    setEditMode(false);
    setCurrentRecord(null);
    form.resetFields(); // Clear the form fields
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      eventName: record.eventName,
      eventlocation: record.eventlocation,
      eventDate: (record.eventDate || record.createdAt) ? moment.utc(record.eventDate || record.createdAt) : null,
      incharge_1: record.incharge_1,
      incharge_2: record.incharge_2,
      incharge_3: record.incharge_3,
    });
    setEditMode(true);
    setCurrentRecord(record);
    setIsModalVisible(true);
  };

  const handleDelete = (key) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this record?',
      onOk: async () => {
        try {
          await api.delete(`/event/deleteEvent/${key}`);
          setData(data.filter(item => item.key !== key));
          message.success('Event deleted successfully');
        } catch (error) {
          console.error('Failed to delete event:', error);
          message.error('Failed to delete event. Please try again later.');
        }
      },
    });
  };

  const handleSearch = e => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter(item =>
    item.eventName.toLowerCase().includes(searchText.toLowerCase()) ||
    item.eventlocation.toLowerCase().includes(searchText.toLowerCase()) ||
    moment(item.eventDate).format('DD-MM-YYYY').includes(searchText.toLowerCase())
  );



  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Events');
    XLSX.writeFile(workbook, 'Events.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Event Details', 10, 10);
    const tableColumn = ['S.No', 'Event Name', 'Location', 'Date', 'Incharge 1', 'Incharge 2', 'Incharge 3'];
    const tableRows = filteredData.map((item, index) => [
      index + 1, item.eventName, item.eventlocation, moment(item.eventDate).format('DD-MM-YYYY'),
      item.incharge_1, item.incharge_2, item.incharge_3
    ]);
    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save('Events.pdf');
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      render: (text, record, index) => index + 1,
      width: '10%',
    },
    {
      title: 'Event Name',
      dataIndex: 'eventName',
      key: 'eventName',
      width: '30%',
    },
    {
      title: 'Event Location',
      dataIndex: 'eventlocation',
      key: 'eventlocation',
      width: '30%',
    },
    {
      title: 'Event Date',
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: '20%',
      render: (date, record) => {
        const dateVal = date || record?.createdAt;
        return dateVal ? moment.utc(dateVal).format('DD-MM-YYYY') : 'N/A';
      },
    },
    {
      title: 'View/Edit',
      key: 'viewEdit',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)} />
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
        </Space>
      ),
      width: '10%',
    },
    {
      title: 'Delete',
      key: 'delete',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record.key)} />
      ),
      width: '10%',
    },
  ];

  const handleView = (record) => {
    setViewRecord(record);
    setIsViewModalVisible(true);
  };

  const handleViewModalCancel = () => {
    setIsViewModalVisible(false);
  };

  const onChange = (pagination, filters, sorter, extra) => {
    console.log('params', pagination, filters, sorter, extra);
  };

  return (
    <div style={{ padding: '20px', boxSizing: 'border-box' }}>
      <h1 style={{ color: 'seagreen', textAlign: 'center', marginBottom: '20px' }}>
        <MdEvent /> Events
      </h1>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        <Col span={12}>
          <Input
            placeholder="Search events"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ width: '50%' }}
          />
           <Button icon={<FileExcelOutlined />} onClick={exportToExcel} style={{ marginRight: '10px' }}>Export Excel</Button>
           <Button icon={<FilePdfOutlined />} onClick={exportToPDF} style={{ marginRight: '10px' }}>Export PDF</Button>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddEventClick}
          >
            Add Event
          </Button>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={filteredData}
        onChange={onChange}
        rowKey="key"
      />
      <Modal
        title={editMode ? 'Edit Event' : 'Add New Event'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Submit
          </Button>,
        ]}
      >
        <Form
          form={form}
          name="eventForm"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item
            label="Event Name"
            name="eventName"
            rules={[{ required: true, message: 'Please input the Event Name!' }]}
          >
            <Input autoComplete='off' />
          </Form.Item>
          <Form.Item
            label="Event Location"
            name="eventlocation"
            rules={[{ required: true, message: 'Please input the Event Location!' }]}
          >
            <Input autoComplete='off' />
          </Form.Item>
          <Form.Item
            label="Incharge 1"
            name="incharge_1"
            rules={[{ message: 'Please input the Event Incharge 1!' }]}
          >
            <Input autoComplete='off' />
          </Form.Item>
          <Form.Item
            label="Incharge 2"
            name="incharge_2"
            rules={[{ message: 'Please input the Event Incharge 2!' }]}
          >
            <Input autoComplete='off' />
          </Form.Item>
          <Form.Item
            label="Incharge 3"
            name="incharge_3"
            rules={[{ message: 'Please input the Event Incharge 3!' }]}
          >
            <Input autoComplete='off' />
          </Form.Item>
          <Form.Item
            label="Event Date"
            name="eventDate"
            rules={[{ required: true, message: 'Please select the Event Date!' }]}
          >
            <DatePicker format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Event Details"
        open={isViewModalVisible}
        onCancel={handleViewModalCancel}
        footer={[
          <Button key="ok" onClick={handleViewModalCancel}>
            OK
          </Button>,
        ]}
      >
        {viewRecord && (
          <div>
            <p><strong>Event Name:</strong> {viewRecord.eventName}</p>
            <p><strong>Event Location:</strong> {viewRecord.eventlocation}</p>
            <p><strong>Event Date:</strong> {moment(viewRecord.eventDate).format('YYYY-MM-DD')}</p>
            <p><strong>Incharge 1:</strong> {viewRecord.incharge_1}</p>
            <p><strong>Incharge 2:</strong> {viewRecord.incharge_2}</p>
            <p><strong>Incharge 3:</strong> {viewRecord.incharge_3}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EventRegistration;
